<template>
  <div
    class="grid items-center gap-4 bg-white px-4 py-4 sm:grid-cols-[10rem_1fr_14rem_14rem_11rem] sm:px-5"
  >
    <div>
      <p
        class="inline-flex h-fit rounded-full bg-gray-100 px-2 text-xs leading-5 font-medium text-gray-800"
      >
        {{ timeAgo.format(new Date(usage.createdDate)) }}
      </p>
      <p class="mt-1 ml-2 flex items-center gap-1 text-xs text-gray-500">
        {{ createdDate }}
      </p>
    </div>
    <div class="max-w-92">
      <div class="flex">
        <p class="text-sm font-medium text-indigo-600">
          {{ usage.descriptor.vacancy.title }}
        </p>
      </div>
      <p class="mt-1 flex items-center text-xs text-gray-500">
        {{ usage.descriptor.company.name }}
      </p>
    </div>
    <div class="flex flex-col gap-1">
      <div class="flex justify-start gap-x-2">
        <span
          v-show="usage.descriptor.vacancy?.id"
          class="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800"
        >
          {{ t('vacancy.label') }} {{ usage.descriptor.vacancy?.id }}
        </span>
        <span
          v-show="usage.descriptor.publication?.published"
          class="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800"
        >
          {{
            startsInFuture
              ? t('vacancy.publication.badges.toBePublished')
              : t('vacancy.publication.badges.published')
          }}
        </span>

        <span
          v-show="!usage.descriptor.publication?.published"
          class="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800"
        >
          {{ t('company.subscription.expired') }}
        </span>
      </div>
      <div class="mt-1 sm:flex sm:justify-between">
        <div
          class="mt-2 flex items-center text-xs text-gray-500 sm:mt-0 sm:ml-1"
        >
          <span>
            {{ usage.descriptor.site.name }}
          </span>
        </div>
      </div>
      <div class="mt-1 sm:flex sm:justify-between">
        <div
          class="mt-2 flex items-center text-xs text-gray-500 sm:mt-0 sm:ml-1"
        >
          <span class="flex gap-x-1">
            <CalendarDaysIcon class="h-4 w-4" />
            {{ startDate }}
          </span>
          <span class="px-0.5">-</span>
          <span>
            {{ endDate }}
          </span>
        </div>
      </div>
    </div>
    <div class="text-left text-xs text-gray-500">
      <p v-if="!usage.reversed">
        {{
          t('company.subscription.incrementalCreditUsed', {
            count: usage.usedCredits,
            total: feature.planCredits,
          })
        }}
      </p>
      <div v-else class="">
        <p class="self-center text-left text-xs">
          <span class="font-medium text-indigo-600"
            >{{ t('company.subscription.creditRestored') }}:
          </span>
          <span class="text-gray-500"> {{ lastModifiedDate }}</span>
        </p>
        <p
          class="mt-1 overflow-hidden text-left text-xs text-ellipsis whitespace-nowrap text-gray-500"
        >
          by: {{ usage.lastModifiedBy }}
        </p>
      </div>
      <p v-if="usage.descriptor.slot?.start" class="mt-1 text-xs">
        slot:
        {{ format(new Date(usage.descriptor.slot.start), 'dd/MM/yyyy') }}-{{
          format(new Date(usage.descriptor.slot.end), 'dd/MM/yyyy')
        }}
      </p>
    </div>
    <div class="flex flex-col items-end gap-x-4">
      <p
        class="inline-flex rounded-full bg-gray-100 px-2 text-xs leading-5 font-medium text-gray-700"
      >
        №{{ usage.id }}
      </p>
      <div v-if="usage.invoiced" class="mt-1 w-full">
        <p class="self-center text-right text-xs font-medium text-green-600">
          {{ t('company.subscription.creditInvoiced') }}
        </p>
        <p class="mt-1 justify-between text-right text-xs text-gray-500">
          {{ lastModifiedDate }}
        </p>
        <p
          class="overflow-hidden text-right text-xs text-ellipsis whitespace-nowrap text-gray-500"
        >
          {{ usage.lastModifiedBy }}
        </p>
      </div>
      <template v-else>
        <button
          v-if="!usage.reversed"
          type="button"
          class="inline-flex items-center gap-x-1.5 rounded-md px-2.5 py-2 text-xs font-semibold"
          :class="
            reversible ? 'cursor-pointer text-indigo-600' : 'text-gray-500'
          "
          :disabled="!reversible || subscriptionStore.isSaving"
          @click="revertUsage"
        >
          <ArrowUturnLeftIcon class="h-4 w-4" aria-hidden="true" />
          {{ t('company.subscription.restoreCredit') }}
          <HelpPopover
            v-if="!reversible"
            :text="t('company.subscription.restoreDisabled')"
            placement="left"
          />
        </button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { format, parseISO } from 'date-fns'
import { CalendarDaysIcon, ArrowUturnLeftIcon } from '@heroicons/vue/24/outline'
import HelpPopover from '../HelpPopover.vue'
import TimeAgo from 'javascript-time-ago'
import { useSubscriptionStore } from '../../store/subscriptionStore'

const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global',
})
const subscriptionStore = useSubscriptionStore()
const props = defineProps({
  usage: {
    required: true,
    type: Object,
  },
  feature: {
    required: true,
    type: Object,
  },
  reversible: {
    type: Boolean,
    default: false,
  },
})

const timeAgo = new TimeAgo()

const revertUsage = () => {
  subscriptionStore.revertSubscriptionFeatureUsage(props.usage)
}

const createdDate = computed(() => {
  return format(props.usage.createdDate, 'dd/MM/yyyy')
})
const lastModifiedDate = computed(() => {
  return props.usage.lastModifiedDate
    ? format(props.usage.lastModifiedDate, 'dd/MM/yyyy')
    : ''
})
const startDate = computed(() => {
  return format(props.usage.descriptor.publication.startDate, 'dd/MM/yyyy')
})
const endDate = computed(() => {
  if (props.usage.descriptor.publication.endDate.startsWith('2999'))
    return t('vacancy.publication.noEndDate')

  return format(
    parseISO(props.usage.descriptor.publication.endDate),
    'dd/MM/yyyy',
  )
})
const startsInFuture = computed(() => {
  const startDate = parseISO(props.usage.descriptor.publication.startDate)
  return startDate > new Date()
})
</script>
